import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import HooksModalV5Key from "app/constants/HooksModalV5Key";
import ManagementCard
    from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/ManagementCard";
import {
    StyledManagementCardIconButton
} from "app/pages/sales-intelligence/modules/manage-integrations/components/cards-components/styles";
import MCPConnectionBanner
    from "app/pages/sales-intelligence/modules/manage-integrations/components/MCPConnectionBanner/MCPConnectionBanner";
import { MCP_CONNECTIONS } from "app/pages/sales-intelligence/modules/manage-integrations/constants/mcp";
import manageIntegrationsTrackingService
    from "app/pages/sales-intelligence/modules/manage-integrations/services/tracking";
import openExternalLink from "app/pages/sales-intelligence/modules/manage-integrations/utils/openExternalLink";
import { openHooksModalV5 } from "app/services/ModalService";
import { isAfterTrial, isNoTouch, isTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import CardsWrapper from "../components/CardsWrapper/CardsWrapper";

type MCPConnectionItem = (typeof MCP_CONNECTIONS)[number];

const MCPServersContainer = (): React.ReactElement => {
    const { translate } = useTranslate();

    const isEnabled = !isTrial(swSettings) && !isAfterTrial(swSettings) && !isNoTouch(swSettings);

    const onUpgradeClick = (toolName?: string) => {
        if (toolName) {
            manageIntegrationsTrackingService.trackMcpUpgrade(toolName);
        }

        openHooksModalV5(HooksModalV5Key.SalesIntelligenceMCP);
    };

    const handleHeaderActionClick = (item: MCPConnectionItem) => {
        if (item.tutorialLink) {
            manageIntegrationsTrackingService.trackMcpGuideClick(item.trackingName);
            openExternalLink(item.tutorialLink);
        }
    };

    const renderCardFooter = (item: MCPConnectionItem) =>
        <Stack>
            {
                isEnabled ? (
                    <StyledManagementCardIconButton
                        buttonHtmlType="button"
                        type="primary"
                        iconName="link-out"
                        placement="right"
                        iconSize="xs"
                        dataAutomation="mcp-servers.connect-button"
                        onClick={() => {
                            manageIntegrationsTrackingService.trackMcpConnect(item.trackingName);
                            openExternalLink(item.connectionLink);
                        }}
                    >
                        {translate("si.common.connect")}
                    </StyledManagementCardIconButton>
                ) : (
                    <StyledManagementCardIconButton
                        buttonHtmlType="button"
                        type="trial"
                        iconName="sparkles-gray"
                        iconSize="xs"
                        data-automation="mcp-servers.integration-card-upgrade-button"
                        onClick={() => onUpgradeClick(item.trackingName)}
                    >
                        {translate("si.common.contact_us")}
                    </StyledManagementCardIconButton>
                )
            }
        </Stack>

    return (
        <Flex
            flexDirection="column"
            gap="24px"
        >
            <MCPConnectionBanner
                isTrialButton={!isEnabled}
                onUpgrade={onUpgradeClick}
            />

            <CardsWrapper>
                {MCP_CONNECTIONS.map((item) => (
                    <ManagementCard
                        key={item.titleKey}
                        dataAutomation={item.dataAutomation}
                        title={translate(item.titleKey)}
                        titleIconName={item.iconName}
                        titleIconColor={item.iconColor}
                        onHeaderActionClick={item.tutorialLink ? () => handleHeaderActionClick(item) : undefined}
                        footer={renderCardFooter(item)}
                    >
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon["300"]}
                        >
                            {translate(item.descriptionKey)}
                        </Text>
                    </ManagementCard>
                ))}
            </CardsWrapper>
        </Flex>
    );
};

export default MCPServersContainer;
